import { memo, type ReactNode, useCallback, useId, useMemo, useState } from 'react';

import { ChevronDownIcon, type IconComponent } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type WorkspaceAgentClarifyHotkeyGuard, WorkspaceAgentClarifyOption } from './WorkspaceAgentClarifyOption';
import { type WorkspaceAgentClarifyOptionInput } from './workspaceAgentClarifyChoice';

/** Position in a run of questions, e.g. `1 of 2`. */
export type WorkspaceAgentClarifyStep = {
  current: number;
  total: number;
};

export type WorkspaceAgentClarifyMessageProps = {
  /** Caller-owned controls under the options, such as Cancel. Hidden once the question is answered. */
  actionButtons?: ReactNode;
  'data-testid'?: string;
  /**
   * Binds 1–9 to the options in order. Off by default so a clarify card in a thread does not take
   * those keys unless the caller is a chooser that owns them.
   */
  enableNumberHotkeys?: boolean;
  /** Off by default. Guided save-location opts in so digits work while the composer is focused. */
  enableNumberHotkeysOnContentEditable?: boolean;
  /** Omit when the card should not show a glyph (e.g. guided-variant save location). */
  icon?: IconComponent;
  /** Absent once the question has been answered, which is what makes the card read-only. */
  onSelect?: (optionId: string) => void;
  optionTestId?: string;
  options: readonly WorkspaceAgentClarifyOptionInput[];
  /** Omit when the parent renders the question outside the card (e.g. guided-variant chooser). */
  question?: string;
  questionTestId?: string;
  /** The answer already given, matched by option id (or by the label, when options are strings). */
  selectedOption?: string;
  /** When set, a number key is ignored unless this returns true. */
  shouldHandleNumberHotkey?: WorkspaceAgentClarifyHotkeyGuard;
  step?: WorkspaceAgentClarifyStep;
};

/**
 * A question the agent needs answered before it can carry on.
 *
 * Answering does not remove the card, it collapses it to the choice that was made — the thread is a
 * record of the run, and a question that vanishes takes the reason for what followed with it. The
 * alternatives stay reachable behind a disclosure for the same reason.
 */
export const WorkspaceAgentClarifyMessage = memo(
  ({
    actionButtons,
    'data-testid': testId,
    enableNumberHotkeys = false,
    enableNumberHotkeysOnContentEditable = false,
    icon: Icon,
    onSelect,
    optionTestId,
    options,
    question,
    questionTestId,
    selectedOption,
    shouldHandleNumberHotkey,
    step,
  }: WorkspaceAgentClarifyMessageProps) => {
    const [isShowingAllOptions, setIsShowingAllOptions] = useState(false);
    const optionsId = useId();
    const choices = useMemo(
      () => options.map((option) => (typeof option === 'string' ? { id: option, label: option } : option)),
      [options],
    );

    const handleToggleOptions = useCallback(() => setIsShowingAllOptions((value) => !value), []);

    const isAnswered = selectedOption !== undefined;
    const selectedIndex = isAnswered ? choices.findIndex((choice) => choice.id === selectedOption) : -1;
    const selectedLabel = selectedIndex === -1 ? selectedOption : (choices[selectedIndex]?.label ?? selectedOption);
    const canShowAlternatives = isAnswered && choices.length > 1;

    return (
      <div className="flex flex-col gap-2 rounded-12 bg-gray-2 p-3" data-testid={testId}>
        {question ? (
          <div className="flex min-w-0 items-center gap-1 px-2 py-1">
            {Icon ? <Icon className="shrink-0 text-gray-10" size="xs" /> : null}
            <p
              className="min-w-0 flex-1 break-words font-semibold text-12 text-gray-12 leading-4"
              data-testid={questionTestId}
            >
              {question}
            </p>
            {step ? (
              <span className="shrink-0 text-12 text-gray-9 leading-4">
                {step.current} of {step.total}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2" id={optionsId}>
            {isAnswered && !isShowingAllOptions && selectedLabel ? (
              <WorkspaceAgentClarifyOption
                // A selection that is not one of the options — an abandoned run, for one — has no
                // position to show, and no badge reads better than a wrong one.
                data-testid={optionTestId}
                index={selectedIndex === -1 ? undefined : selectedIndex}
                isSelected
                label={selectedLabel}
                optionId={selectedIndex === -1 ? undefined : selectedOption}
              />
            ) : (
              choices.map((choice, index) => (
                <WorkspaceAgentClarifyOption
                  data-testid={optionTestId}
                  enableOnContentEditable={enableNumberHotkeysOnContentEditable}
                  hotkey={enableNumberHotkeys ? String(index + 1) : undefined}
                  index={index}
                  isSelected={choice.id === selectedOption}
                  key={choice.id}
                  label={choice.label}
                  onSelect={onSelect}
                  optionId={choice.id}
                  shouldHandleHotkey={shouldHandleNumberHotkey}
                />
              ))
            )}
          </div>

          {canShowAlternatives ? (
            <button
              aria-controls={optionsId}
              aria-expanded={isShowingAllOptions}
              className="inline-flex items-center gap-0.5 self-start py-1 font-medium text-12 text-gray-10 leading-4 hover:text-gray-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altitude-8"
              onClick={handleToggleOptions}
              type="button"
            >
              {isShowingAllOptions ? 'Hide other options' : 'Show other options'}
              <ChevronDownIcon className={cn('transition-transform', isShowingAllOptions && 'rotate-180')} size="sm" />
            </button>
          ) : null}

          {actionButtons && !isAnswered ? actionButtons : null}
        </div>
      </div>
    );
  },
);

WorkspaceAgentClarifyMessage.displayName = 'WorkspaceAgentClarifyMessage';
